// ─── Sanctum Image Service ─────────────────────────────────────────────────────
// Fetches destination photography from the production worker endpoint
// GET /api/images?query=<q>&count=<n> → { images: [{ url, thumb, alt, photographer, photographerUrl, downloadLocation }] }
//
// The API key lives server-side only. In local dev (no worker running) the
// fetch will fail or the endpoint will return { images: [] } — in either case
// we resolve to an empty array silently so every consumer can degrade
// gracefully to the existing gradient/emoji look. No broken <img> icons, no
// layout jumps.

export interface Img {
  url: string
  thumb: string
  alt: string
  photographer: string
  photographerUrl: string
  downloadLocation?: string
}

interface Destination {
  imageQuery?: string
}

// ─── In-memory cache (per query) ──────────────────────────────────────────────
const cache = new Map<string, Img[]>()
const inflight = new Map<string, Promise<Img[]>>()

export async function fetchImages(query: string, count = 4): Promise<Img[]> {
  if (!query) return []
  const cacheKey = `${query}::${count}`

  const cached = cache.get(cacheKey)
  if (cached) return cached

  const pending = inflight.get(cacheKey)
  if (pending) return pending

  const request = (async (): Promise<Img[]> => {
    try {
      const res = await fetch(`/api/images?query=${encodeURIComponent(query)}&count=${count}`)
      if (!res.ok) return []
      const data = await res.json()
      const images: Img[] = Array.isArray(data?.images) ? data.images : []
      cache.set(cacheKey, images)
      return images
    } catch {
      // No worker in dev, network error, etc — fail silently.
      cache.set(cacheKey, [])
      return []
    } finally {
      inflight.delete(cacheKey)
    }
  })()

  inflight.set(cacheKey, request)
  return request
}

export async function getDestinationImages(dest: Destination, count = 4): Promise<Img[]> {
  if (!dest.imageQuery) return []
  return fetchImages(dest.imageQuery, count)
}
